// @flow
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import { Field, FormikBag, getIn } from 'formik';

import AccountTypeSelector from 'components/Formik/AccountTypeSelector';
import FormikTextField from 'components/Formik/TextField';

import { getAccountsById } from 'data/accounts/selectors';
import AccountActionSelector from './AccountActionSelector';

import { ACCOUNT_ACTION_ADD, ACCOUNT_ACTION_IGNORE } from '../../types';
import type { AccountAction, AccountData } from '../../types';

import { useStyles } from './styles';

type Props = {
  account: AccountData,
  index: number,

  otherNames: Object,

  formik: FormikBag,
}

const AccountRow = (props: Props) => {
  const { account, formik, index } = props;

  const nameRef = useRef();

  const accounts = useSelector(getAccountsById);
  const [errorOutline, setErrorOutline] = useState();
  const classes = useStyles();

  const validateOriginalName = (name) => {
    const { otherNames } = props;
    let ret = null;
    const lowerCaseName = name.toLowerCase();
    accounts.forEach((otherAccount) => {
      if (otherAccount.name.toLowerCase() === lowerCaseName) {
        setErrorOutline(true);
        ret = "Account name must be unique.";
      }
    });

    let count = 0;
    otherNames.forEach((stagedName) => {
      if (stagedName === lowerCaseName) {
        count += 1;
      }
    });
    if (count > 1) {
      setErrorOutline(true);
      ret = "Account name must be unique.";
    }
    !ret && setErrorOutline(false);
    return ret;
  };

  const validateType = (type) => {
    let ret = null;
    if (type.qcsType === "UNKNOWN") {
      ret = `Please pick an account type`;
    }
    return ret;
  };

  const getNameFieldName = () => `${account.formFieldName}.name`;
  const getTypeFieldName = () => `${account.formFieldName}.type`;
  const getActionFieldName = () => `${account.formFieldName}.action`;

  const getDiscoveredAccountName = () => account.discoveredAccount.name;

  const getMaskedNumber = () => {
    const { maskedNumber } = account.discoveredAccount;
    if (!maskedNumber) {
      return undefined;
    }
    const charactersNum = maskedNumber.length >= 4 ? 4 : maskedNumber.length;
    const lastCharacters = maskedNumber && maskedNumber.slice(-charactersNum);
    const last4Characters = (lastCharacters && !Number.isNaN(Number(lastCharacters))) ? 'X'.repeat(4 - charactersNum) + lastCharacters : 'XXXX';
    return `...${last4Characters}`;
  };

  const handleAccountActionChange = (event: Object, child?: Object) => {
    const nameFieldName = getNameFieldName();
    const actionFieldName = getActionFieldName();

    const prevAction = getIn(formik.values, actionFieldName);
    const newAction: AccountAction = event.target.value;

    if ((newAction === ACCOUNT_ACTION_ADD || newAction === ACCOUNT_ACTION_IGNORE) && prevAction.type === 'LINK_TO') {
      formik.setFieldValue(nameFieldName, account.name);
    }
    if (newAction.type === 'LINK_TO') {
      formik.setFieldValue(nameFieldName, newAction.accountToLinkTo ? newAction.accountToLinkTo.name : 'unknown');
    }

    formik.handleChange(event, child);
  };

  const nameFieldName = getNameFieldName();
  const typeFieldName = getTypeFieldName();
  const actionFieldName = getActionFieldName();

  const discoveredAccountName = getDiscoveredAccountName();
  const maskedNumber = getMaskedNumber();
  const name = getIn(formik.values, nameFieldName);
  const action = getIn(formik.values, actionFieldName);

  const badType = getIn(formik.errors, typeFieldName);

  // field is disabled: ignore & link_to
  // link_to shows discovered name and linked_to name
  // add allows name to be edited and shows discovered name
  // ignore only shows discovered name

  const disableAccountName = action.type === 'LINK_TO';
  const ignore = action.type === "IGNORE";

  const showNameTip = nameRef.current && (nameRef.current.offsetWidth < nameRef.current.scrollWidth);
  const showLinkToInfo = action.type === 'LINK_TO';

  let additionalNameInfo;
  if (showLinkToInfo) {
    additionalNameInfo = `Link to: ${action.accountToLinkTo.name}`;
  } else if (name !== discoveredAccountName) {
    additionalNameInfo = `Name at Bank: ${discoveredAccountName}`;
    if (maskedNumber) {
      additionalNameInfo += ` (${maskedNumber})`;
    }
  } else if (maskedNumber) {
    additionalNameInfo = maskedNumber;
  }

  return (
    <TableRow className={classes.tableRow}>
      <TableCell component="th" scope="row" className={classes.nameCell}>
        <div className={classes.nameSpace}>
          <Tooltip title={(showNameTip && !ignore) ? name : ''}>
            <div
              style={{ marginRight: 40 }}
              // don't remove this div - it is important for catching the tooltip ref, as field
              // is a 3rd party functional component and therefore cannot hold a ref
            >
              <Field
                component={FormikTextField}
                disabled={disableAccountName}
                fullWidth
                name={nameFieldName}
                inputProps={{
                  id: `account#${index}name`,
                  className: classes.nameInput,
                  disabled: ignore,
                  ref: nameRef,
                }}
                variant="outlined"
                className={classNames(ignore ? classes.ignoreFields : classes.fields, errorOutline && classes.errorOutline)}
                validate={(action.type === 'ADD_ACCOUNT') ? validateOriginalName : undefined}
              />
            </div>
          </Tooltip>
          <Typography
            variant="caption"
            className={classNames(ignore ? classes.greyAccDetails : classes.accDetails,
              errorOutline && classes.paddedSubheader)}>
            {additionalNameInfo}
          </Typography>
        </div>
      </TableCell>
      <TableCell className={classes.typeCell}>
        <div className={classes.nameSpace}>
          <AccountTypeSelector
            className={classNames(ignore ? classes.ignoreTypeBox : classes.typeBox,
              errorOutline && classes.errorPadding)}
            currentAccountType={account.type}
            disabled={ignore}
            fullWidth
            id={`account#${index}type`}
            name={typeFieldName}
            noIcon
            validate={(action.type !== 'IGNORE') ? validateType : undefined}
          />
          <Typography variant="caption" className={classes.errorType}>
            {action.type !== 'IGNORE' && badType}
          </Typography>
        </div>
      </TableCell>
      <TableCell className={classes.actionCell} align="right">
        <AccountActionSelector
          disableUnderline
          id={`account#${String(index)}action`}
          linkableAccountsSection1={account.linkableAccountsSection1}
          linkableAccountsSection2={account.linkableAccountsSection2}
          linkableAccountsSection3={account.linkableAccountsSection3}
          name={actionFieldName}
          onBlur={formik.handleBlur}
          onChange={handleAccountActionChange}
          value={action}
        />
      </TableCell>
    </TableRow>
  );
}

export default AccountRow;
